import './overzicht.scss';

const getPagedFromUrl = (href) => {
	try {
		const url = new URL(href, location.origin);
		const qp = url.searchParams.get('paged') || url.searchParams.get('page');
		if (qp) {
			return parseInt(qp, 10) || 1;
		}
		const m = url.pathname.match(/(?:\/page\/)(\d+)\/?$/i);
		if (m) {
			return parseInt(m[1], 10) || 1;
		}
		const segs = url.pathname.replace(/\/$/, '').split('/');
		const last = segs[segs.length - 1];
		if (/^\d+$/.test(last)) {
			return parseInt(last, 10);
		}
	} catch (err) {
		console.warn('getPagedFromUrl error', err);
	}
	return 1;
};

const getOverviewResults = (form) => form.querySelector('[data-overzicht-results]');
const getOverviewCards = (scope) => scope.querySelector('[data-overzicht-cards]');
const getLoadMoreButton = (scope) => scope.querySelector('[data-overzicht-load-more]');

const buildOverviewRequestUrl = (form, paged) => {
	const endpoint = form.dataset.endpoint || '/wp-json/avb/v1/overview';
	const params = new URLSearchParams(new FormData(form));
	params.set('paged', String(paged));

	return `${endpoint}?${params.toString()}`;
};

const observers = new WeakMap();
const loadingForms = new WeakSet();

const disconnectLazyloadObserver = (form) => {
	const observer = observers.get(form);
	if (observer) {
		observer.disconnect();
		observers.delete(form);
	}
};

const observeLazyload = (form) => {
	disconnectLazyloadObserver(form);

	const loadOption = form.dataset.loadOption || form.querySelector('[name="load_option"]')?.value;
	if (loadOption !== 'lazyload') {
		return;
	}

	const button = getLoadMoreButton(form);
	if (!button || typeof IntersectionObserver === 'undefined') {
		return;
	}

	const sentinel = form.querySelector('[data-overzicht-lazyload-sentinel]') || button;

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting || loadingForms.has(form)) {
					return;
				}

				const nextButton = getLoadMoreButton(form);
				if (!nextButton) {
					disconnectLazyloadObserver(form);
					return;
				}

				const nextPage = parseInt(nextButton.dataset.nextPage || '1', 10) || 1;
				updateOverview(form, nextPage, 'append');
			});
		},
		{
			root: null,
			rootMargin: '200px 0px',
			threshold: 0,
		}
	);

	observer.observe(sentinel);
	observers.set(form, observer);
};

const updateOverview = (form, paged, mode = 'replace') => {
	const results = getOverviewResults(form);
	if (!results || loadingForms.has(form)) {
		return Promise.resolve();
	}

	loadingForms.add(form);
	form.classList.add('is-loading');
	if (mode === 'append') {
		form.classList.add('is-loading-more');
	}

	const loadMoreButton = getLoadMoreButton(form);
	if (loadMoreButton) {
		loadMoreButton.disabled = true;
		loadMoreButton.setAttribute('aria-busy', 'true');
	}

	return fetch(buildOverviewRequestUrl(form, paged))
		.then((response) => response.json())
		.then((data) => {
			if (mode === 'append') {
				const parser = new DOMParser();
				const nextDoc = parser.parseFromString(data.html, 'text/html');
				const currentCards = getOverviewCards(form);
				const nextCards = getOverviewCards(nextDoc);

				if (!currentCards || !nextCards) {
					results.outerHTML = data.html;
					return;
				}

				currentCards.insertAdjacentHTML('beforeend', nextCards.innerHTML);

				const currentPagination = form.querySelector('[data-overzicht-pagination]');
				const nextPagination = nextDoc.querySelector('[data-overzicht-pagination]');

				if (currentPagination && nextPagination) {
					currentPagination.replaceWith(nextPagination);
				} else if (currentPagination) {
					currentPagination.remove();
				}

				return;
			}

			results.outerHTML = data.html;
		})
		.catch((err) => {
			console.error('overview fetch error', err);
		})
		.finally(() => {
			loadingForms.delete(form);
			form.classList.remove('is-loading', 'is-loading-more');

			const nextButton = getLoadMoreButton(form);
			if (nextButton) {
				nextButton.disabled = false;
				nextButton.removeAttribute('aria-busy');
			}

			observeLazyload(form);
		});
};

document.addEventListener('click', (event) => {
	const loadMoreButton = event.target.closest('[data-overzicht-load-more]');
	if (loadMoreButton) {
		const form = loadMoreButton.closest('[data-overzicht-form]');
		if (!form) {
			return;
		}

		event.preventDefault();
		const nextPage = parseInt(loadMoreButton.dataset.nextPage || '1', 10) || 1;
		updateOverview(form, nextPage, 'append');
		return;
	}

	const link = event.target.closest('[data-overzicht-pagination] a');
	if (!link) {
		return;
	}

	const form = link.closest('[data-overzicht-form]');
	if (!form) {
		return;
	}

	event.preventDefault();
	updateOverview(form, getPagedFromUrl(link.href), 'replace');
});

document.addEventListener('change', (event) => {
	const filter = event.target.closest('[data-overzicht-filter]');
	if (!filter) {
		return;
	}

	const form = filter.closest('[data-overzicht-form]');
	if (!form) {
		return;
	}

	updateOverview(form, 1, 'replace');
});

const initLazyloadOverviews = () => {
	document.querySelectorAll('[data-overzicht-form]').forEach((form) => {
		observeLazyload(form);
	});
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initLazyloadOverviews);
} else {
	initLazyloadOverviews();
}
